import {Component, OnInit, OnDestroy, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {Sort} from '@angular/material/sort';
import {MatDialog} from '@angular/material/dialog';
import {MatTableDataSource} from '@angular/material/table';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {getSortDirection} from '../../../../../common/util/Util';
import {DateUtil} from '../../../../../common/util/DateUtil';
import {TableControl} from '../../../../../common/models/TableControl';
import {EntRequestBuilder} from '../../../../services/ent-request-builder';
import {PagedResponseDTO} from '../../../../../common/models/PagedResponseDTO';
import {PagedRequestDTO} from '../../../../../common/models/PagedRequestDTO';
import {ProviderNetworkDTO} from '../../../../models/ProviderNetworkDTO';
import {ProviderNetworkService} from '../../../../services/provider-network.service';
import {SharedService} from '../../../../../common/services/shared-service';
import {
  ConfirmationDialogComponent
} from '../../../../../common/dialogs/confirmation-dialog/confirmation-dialog.component';
import {detailsExpand} from '../../../../../common';
import {UserAuthorisationService} from 'src/app/modules/common/services/user-authorisation.service';

@Component({
  selector: 'app-provider-networks',
  templateUrl: './provider-networks.component.html',
  styleUrls: [
    '../../../../../../../styles/global/_management-styles.scss',
    '../../../../../../../styles/global/_add-view-edit-styles.scss'
  ],
  animations: [detailsExpand]
})
export class ProviderNetworksComponent implements OnInit, OnDestroy {
  readonly destroyed$ = new Subject();
  readonly dateFormat = DateUtil.dateFormat;
  readonly dateTimeFormat = DateUtil.dateTimeFormat;
  hasPermissionsBasedOnRole: boolean;

  providerNetworkDataSource: MatTableDataSource<ProviderNetworkDTO> = new MatTableDataSource();
  providerNetworkExpandedRow: ProviderNetworkDTO;
  providerNetworkTableControl: TableControl;
  providerNetworkDisplayedColumns: string[] = [
    'icons',
    'providerNetworkId',
    'name',
    'code',
    'administrator',
    'startDate',
    'endDate'
  ];

  // MatPaginator
  @ViewChild('providerNetworkPaginator', {static: false}) providerNetworkPaginator: MatPaginator;

  constructor(
    private dialog: MatDialog,
    private providerNetworkService: ProviderNetworkService,
    private router: Router,
    private sharedService: SharedService,
    private userAuthorisationService: UserAuthorisationService
  ) {
    const controls = EntRequestBuilder.getTableControls();

    this.providerNetworkTableControl = new TableControl(
      controls.length,
      controls.pageSize,
      controls.pageNumber,
      controls.pageSizeOptions,
      'providerNetworkId',
      'DESCENDING',
      'name',
      ''
    );

    this.hasPermissionsBasedOnRole = this.userAuthorisationService.hasRoleEnterpriseManager();
  }

  ngOnInit(): void {
    this.getFilteredListOfProviderNetwork(this.providerNetworkTableControl);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  pageChanged($event: PageEvent) {
    this.providerNetworkTableControl.pageNumber = $event.pageIndex;
    this.providerNetworkTableControl.pageSize = $event.pageSize;
    this.getFilteredListOfProviderNetwork(this.providerNetworkTableControl);
  }

  editEntry(event: MouseEvent, providerNetwork: ProviderNetworkDTO) {
    event.preventDefault();
    event.stopPropagation();

    this.router.navigate([`enterprise-management/edit-provider-network/${providerNetwork.providerNetworkId}`]);
  }

  onDeleteProviderNetworkConfirmation(event: MouseEvent, providerNetwork: ProviderNetworkDTO) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.hasPermissionsBasedOnRole) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '40vw',
      data: `Are you sure you wish to delete this provider ${providerNetwork.name} network?`
    });

    dialogRef
      .afterClosed()
      .subscribe((isConfirmed) => {
        if (isConfirmed) {
          this.onDeleteProviderNetworkProductConfirmed(providerNetwork.providerNetworkId);
        }
      });
  }

  onDeleteProviderNetworkProductConfirmed(providerNetworkId: bigint) {
    this.providerNetworkService.deleteProviderNetworkBy(providerNetworkId)
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe(
        (response) => {
          this.sharedService.openSnackBar(`Provider Network with ${providerNetworkId} deleted`, 'Dismiss');
          this.getFilteredListOfProviderNetwork(this.providerNetworkTableControl);
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  onValueChanged(value: string) {
    this.providerNetworkTableControl.filterField = 'name';
    this.providerNetworkTableControl.filterValue = value;
    this.getFilteredListOfProviderNetwork(this.providerNetworkTableControl);
  }

  sortData($event: Sort) {
    if (!$event.direction) {
      return;
    }
    if (this.providerNetworkPaginator != null) {
      this.providerNetworkPaginator.firstPage();
      this.providerNetworkTableControl.pageNumber = 0;
    }
    if ($event.active === 'administrator') {
      this.providerNetworkTableControl.sortField = 'administratorByAdministratorId.name';
    }
    else {
      this.providerNetworkTableControl.sortField = $event.active;
    }
    this.providerNetworkTableControl.sortDirection = getSortDirection($event.direction);
    this.getFilteredListOfProviderNetwork(this.providerNetworkTableControl);
  }

  getFilteredListOfProviderNetwork(tableControl: TableControl): void {
    let pagedRequestDTO: PagedRequestDTO;
    pagedRequestDTO = EntRequestBuilder.getProviderNetworkPagedRequestDTO(tableControl);

    this.providerNetworkService
      .getListOfProviderNetwork(pagedRequestDTO)
      .pipe(
        takeUntil(this.destroyed$),
      )
      .subscribe(
        (response: PagedResponseDTO<ProviderNetworkDTO>) => {
          this.providerNetworkDataSource.data = response.resultList;
          this.providerNetworkTableControl.length = response.totalRecords;
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

}
